import { FloorplanPlacementGeometry } from '../types';

/**
 * Coordinate math for the read-only floorplan overlay.
 *
 * **Copied, not shared.** The authoring canvas in `resource-booking-admin` has the same math in
 * `src/app/modules/floorplan-builder/utils/coordinates.ts`. There is no shared `@hqo/*` package for
 * it, and publishing one to share ~20 lines of arithmetic would add a release step to two repos and
 * couple this app's build to it. Copying matches the Phase-1 precedent of per-module copies and
 * keeps this repo dependency-free. Revisit only if Phase 3 (tenant-portal) also needs it natively —
 * then one package for all three consumers starts to pay for itself.
 *
 * Only the read-only half is copied: the admin's draw/resize helpers (`rectFromPoints`,
 * `resizeRect`, `translateRect`, `enforceMinSize`, `clientPointToNormalized`) have no consumer here,
 * and copying them would be dead code that silently drifts from the original.
 */

export const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

// Percentages are rounded before they reach CSS: subtracting normalized values reintroduces float
// error (`1 - 0.9` is `0.09999999999999998`), which would emit `9.999999999999998%` into the DOM.
// Four decimals is 1/10000th of the container — far below one device pixel on any plausible image.
const PERCENT_PRECISION = 1e4;

export const toPercent = (fraction: number): string =>
  `${Math.round(fraction * 100 * PERCENT_PRECISION) / PERCENT_PRECISION}%`;

export interface PercentBox {
  left: string;
  top: string;
  width: string;
  height: string;
  /** Clockwise rotation around the box's centre; absent for an unrotated placement. */
  transform?: string;
}

/**
 * Normalized geometry → CSS percentages.
 *
 * Percentages rather than pixels on purpose: the overlay is absolutely positioned inside a box that
 * shares the image's aspect ratio, so a percentage box tracks the image at any viewport width with
 * no measuring, no resize listener and nothing to re-layout on rotation.
 *
 * Values are clamped so a rect the admin placed flush against an edge (or a row that slipped
 * out of range) cannot push the overlay outside its container. A rotated placement turns around its
 * own centre; x/y/width/height describe it before the turn.
 */
export const geometryToPercentBox = ({ x, y, width, height, rotation }: FloorplanPlacementGeometry): PercentBox => {
  const left = clamp01(x);
  const top = clamp01(y);

  return {
    left: toPercent(left),
    top: toPercent(top),
    // Clamped against the remaining space so `left + width` can never exceed 100%.
    width: toPercent(Math.min(clamp01(width), 1 - left)),
    height: toPercent(Math.min(clamp01(height), 1 - top)),
    // CSS rotates around the centre by default, which is the pivot the admin rotates around.
    ...(rotation ? { transform: `rotate(${rotation}deg)` } : {}),
  };
};

/** Image aspect ratio for the CSS `aspect-ratio` of the box the overlay is positioned in. */
export const imageAspectRatio = (imageWidth: number, imageHeight: number): number =>
  imageWidth > 0 && imageHeight > 0 ? imageWidth / imageHeight : 1;
